/* ==========================================================================
   Delta rental request — behaviour.

   Rewritten 2026-09-03. The previous file (848 lines) drove an eight-step
   wizard, a second component configurator, a pinned six-frame sequence, a
   horizontal lifecycle rail and a fixed dock. All five are gone, and so is
   their code.

   THE STATE CONTRACT, unchanged and load-bearing:
   the form element IS the state. This file keeps no private copy of the
   request. Everything rendered — the rail, the mobile bar, the figure — is
   derived from the form on demand. The builder hands its details to the dedicated Shopify Forms
   summary field. Without JS, the contact section provides email/phone routes.

   What IS persisted is a restore snapshot in sessionStorage, so a visitor who
   navigates away and comes back does not retype the request. It is written
   from the form and replayed into the form; it never becomes a second source
   of truth while the page is live.
   ========================================================================== */
/* --------------------------------------------------------------------------
   Silent loops in the operational-proof section.

   Deliberately its own IIFE. The request behaviour below returns early when
   `.rr-req` is absent, and the proof section is used on pages that carry no
   request form — folding this in there would silently un-script those pages.

   Nothing here is decorative-only: the <video> sits above an <img> that is the
   real content. If this never runs, the photograph is what the visitor sees,
   which is why the markup ships no `autoplay` attribute. Playback is started
   here so it can be withheld from people who asked for reduced motion or are
   on a metered connection, and so a clip that is off-screen costs nothing.
   -------------------------------------------------------------------------- */
(function () {
  'use strict';

  if (window.__deltaRRLoops) return;
  window.__deltaRRLoops = 1;

  var vids = Array.prototype.slice.call(document.querySelectorAll('video[data-rr-loop]'));
  if (!vids.length) return;

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  if (reduced || conn.saveData) return;

  if (!('IntersectionObserver' in window)) return;

  /* play() rejects when the browser declines to autoplay. That is a normal
     outcome, not an error: the poster stays up and the cell still reads. */
  function start(v) {
    if (v.__rrPending) return;
    v.__rrPending = 1;
    if (v.preload !== 'auto') v.preload = 'auto';
    var p = v.play();
    if (p && p.catch) {
      p.then(function () { v.setAttribute('data-rr-playing', ''); })
       .catch(function () { v.removeAttribute('data-rr-playing'); })
       .then(function () { v.__rrPending = 0; });
    } else {
      v.setAttribute('data-rr-playing', '');
      v.__rrPending = 0;
    }
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      e.target.__rrVisible = e.isIntersecting;
      if (e.isIntersecting) start(e.target);
      else e.target.pause();
    });
  }, { threshold: 0.25 });

  vids.forEach(function (v) { io.observe(v); });

  /* A background tab should not keep decoding. Browsers throttle it anyway,
     but pausing explicitly means returning to the tab restarts cleanly. */
  document.addEventListener('visibilitychange', function () {
    vids.forEach(function (v) {
      if (document.hidden) v.pause();
      else if (v.__rrVisible) start(v);
    });
  });
})();

(function () {
  'use strict';

  /* Both the hero and the request section load this file, so that removing or
     reordering either one in the theme editor cannot silently un-script the
     page — the previous build loaded it from the hero alone. Guard against the
     second execution binding every listener twice. */


  /* Root on the request section itself, not on [data-rr-page]: the hero also
     carries that attribute, and scoping the figure lookups to the hero would
     find nothing. */
  var root = document.querySelector('.rr-req');
  if (!root || root.hasAttribute('data-rr-core-ready')) return;
  root.setAttribute('data-rr-core-ready', 'true');
  document.documentElement.classList.add('rr-js');

  var $ = function (s, c) { return (c || document).querySelector(s); };
  var $$ = function (s, c) { return Array.prototype.slice.call((c || document).querySelectorAll(s)); };

  var form = $('#rr-request-form') || $('form.rr-req__form');
  if (!form) return;

  /* ---------------------------------------------------------------- adapter
     The submission boundary uses the dedicated Shopify Forms inline form.
     This rental request does not enable file uploads. `supportsFiles` is read by the sizing step: while it
     is false, no upload control is rendered anywhere. A control that appears
     to upload but does not is worse than none.
     ------------------------------------------------------------------------ */
  var DeltaRentalRequest = {
    transport: 'shopify-forms',
    supportsFiles: false,
    derived: {
      request_id: 'Request reference',
      package: 'Gear requested',
      components: 'Components'
    }
  };
  window.DeltaRentalRequest = DeltaRentalRequest;

  var STORE_KEY = 'delta-rr-request';

  function save(data) {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(data)); } catch (e) { /* private mode */ }
  }
  function load() {
    try { return JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; }
  }

  /* ------------------------------------------------------------- primitives */
  function radio(name) {
    var el = form.querySelector('input[name="' + name + '"]:checked');
    return el ? el.value : '';
  }
  function val(name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? el.value.trim() : '';
  }
  function announce(msg) {
    var live = $('[data-rr-live]');
    if (live) live.textContent = msg;
  }

  /* --------------------------------------------------------------- analytics
     Events are pushed to dataLayer only if the store already has one. No
     analytics framework is introduced for this page.
     ------------------------------------------------------------------------ */
  function track(name, params) {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') return;
    var payload = { event: 'delta_rental_' + name };
    for (var k in params) if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
    window.dataLayer.push(payload);
  }
  var fired = {};
  function trackOnce(name, params) {
    if (fired[name]) return;
    fired[name] = 1;
    track(name, params);
  }

  /* =========================================================== 02 — PACKAGE */
  var PACKAGE_PARTS = {
    'full': ['coat', 'pant', 'helmet', 'hood', 'gloves', 'boots'],
    'coatpant': ['coat', 'pant']
  };

  function packageMode() {
    var el = form.querySelector('input[data-rr-package]:checked');
    return el ? el.getAttribute('data-rr-package') : '';
  }

  /* Components actually requested, whichever path produced them. For the two
     dominant packages this is a fixed list; only the third path reads the
     checkboxes. */
  function isSpecialty() { return radio('contact[Rental purpose]') === 'Industrial or aluminized application'; }

  function chosenParts() {
    if (isSpecialty()) return [];
    var mode = packageMode();
    if (PACKAGE_PARTS[mode]) return PACKAGE_PARTS[mode].slice();
    return $$('input[data-rr-component]', form)
      .filter(function (i) { return i.checked; })
      .map(function (i) { return i.getAttribute('data-rr-component'); });
  }

  function partLabel(key) {
    var i = form.querySelector('input[data-rr-component="' + key + '"]');
    return i ? i.value : key;
  }

  /* ------------------------------------------------------------ figure stage
     Only ever runs inside the Individual Components panel. Picks the highest
     scoring photographed set that is a SUBSET of the request, weighting by
     register position so a near-complete photograph beats a sparse one, then
     names in words whatever the photograph does not show.

     A subset — never a superset. Showing a firefighter wearing more than was
     asked for would misrepresent the request.
     ---------------------------------------------------------------------- */
  function partsPanel() { return $('.rr-parts', root) || document.createElement('div'); }
  var figs = $$('[data-rr-fig]', root);
  var stageWrap = $('.rr-parts__stage', root);
  var RANK = ['coat', 'pant', 'helmet', 'hood', 'gloves', 'boots'];

  function figShows(f) {
    var raw = f.getAttribute('data-rr-shows') || '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }
  function score(set) {
    var s = 0;
    for (var i = 0; i < RANK.length; i++) if (set.indexOf(RANK[i]) !== -1) s += Math.pow(2, RANK.length - 1 - i);
    return s;
  }

  function paintFigure() {
    if (!figs.length || !stageWrap) return;
    var want = chosenParts();
    var best = null, bestScore = -1;

    figs.forEach(function (f) {
      var shows = figShows(f);
      var subset = shows.every(function (k) { return want.indexOf(k) !== -1; });
      if (!subset) return;
      var s = score(shows);
      if (s > bestScore) { bestScore = s; best = f; }
    });

    figs.forEach(function (f) { f.setAttribute('data-rr-on', f === best ? 'true' : 'false'); });

    var nameEl = $('[data-rr-figname]', root);
    var gapEl = $('[data-rr-figgap]', root);
    if (!nameEl || !gapEl || !best) return;

    var shown = figShows(best);
    nameEl.textContent = shown.length
      ? shown.map(partLabel).join(' · ')
      : (partsPanel().getAttribute('data-rr-station') || 'Station wear');

    var missing = want.filter(function (k) { return shown.indexOf(k) === -1; });
    var extras = $('[data-rr-extras]', root);
    if (extras) extras.hidden = !missing.length;
    $$('[data-rr-extra]', root).forEach(function (item) {
      item.hidden = missing.indexOf(item.getAttribute('data-rr-extra')) === -1;
    });
    if (missing.length) {
      var tpl = partsPanel().getAttribute('data-rr-gapnote') || 'Your request also covers [LIST].';
      gapEl.textContent = tpl.replace('[LIST]', sentence(missing.map(partLabel)));
      gapEl.hidden = false;
    } else {
      gapEl.textContent = '';
      gapEl.hidden = true;
    }
  }

  function sentence(list) {
    if (list.length === 0) return '';
    if (list.length === 1) return list[0];
    return list.slice(0, -1).join(', ') + ' and ' + list[list.length - 1];
  }

  /* ==================================================== conditional reveals
     data-rr-when="field:value" — shown only while that field holds that value.
     Collapsing RETAINS the inputs inside, so a visitor toggling between paths
     never loses work. Hidden panels are also made inert to keyboard and
     assistive tech, which the previous implementation forgot.
     ---------------------------------------------------------------------- */
  function syncReveals() {
    $$('[data-rr-when]', form).forEach(function (el) {
      var spec = el.getAttribute('data-rr-when');
      var i = spec.indexOf(':');
      var name = spec.slice(0, i);
      var want = spec.slice(i + 1);
      var on = radio(name) === want;
      el.hidden = !on;
      /* Keep the fields out of the tab order and out of validation while the
         panel is closed. Without this a required-looking hidden input can
         block submission with an error the visitor cannot see. */
      $$('input, select, textarea, button, a', el).forEach(function (f) {
        if (f.tagName === 'A') { f.tabIndex = on ? 0 : -1; return; }
        /* The add-wearer button owns its own disabled state (the cap). A
           blanket re-enable here is what let the cap be clicked past. */
        if (f.hasAttribute('data-rr-add-wearer') || f.hasAttribute('data-rr-undo-wearer')) {
          f.disabled = !on || wearerRows().length >= WEARER_CAP;
          return;
        }
        f.disabled = !on;
      });
    });
  }

  /* ============================================================ 03 — QUANTITY */
  var qtyInput = $('[data-rr-qty-input]', form);

  function syncQtyChips() {
    var v = qtyInput ? qtyInput.value.trim() : '';
    $$('[data-rr-qty-chip]', form).forEach(function (c) {
      c.setAttribute('aria-pressed', c.getAttribute('data-rr-qty-chip') === v ? 'true' : 'false');
    });
  }

  $$('[data-rr-qty-chip]', form).forEach(function (chip) {
    chip.addEventListener('click', function () {
      if (!qtyInput) return;
      qtyInput.value = chip.getAttribute('data-rr-qty-chip');
      syncQtyChips();
      update();
      track('quantity_entered', { quantity: qtyInput.value, method: 'chip' });
    });
  });

  /* ============================================================== 04 — WEARERS
     Rows clone and renumber. Above the cap the visitor is pointed at the
     roster path rather than being handed a form with hundreds of inputs.
     ---------------------------------------------------------------------- */
  var WEARER_CAP = 12;
  var wearerWrap = $('[data-rr-wearers]', form);
  var addBtn = $('[data-rr-add-wearer]', form);
  var wearerHint = $('[data-rr-wearer-hint]', form);
  var defaultWearerHint = wearerHint ? wearerHint.textContent : '';
  var removedWearer = null;

  function wearerRows() { return $$('[data-rr-wearer]', wearerWrap || form); }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  /* Extracted so a reload can rebuild the rows before restore() fills them.
     Note the 'input, select' selectors: glove is a <select> and boot a number
     field, because each has its own scale. */
  function addWearerRow(focus) {
    var rows = wearerRows();
    if (!wearerWrap || !rows.length || rows.length >= WEARER_CAP) return null;
    var n = rows.length + 1;
    var clone = rows[0].cloneNode(true);
    clone.setAttribute('data-rr-wearer', String(n));
    var numEl = $('.rr-wear__n', clone);
    if (numEl) numEl.textContent = pad(n);
    $$('input, select', clone).forEach(function (inp) {
      var key = inp.getAttribute('data-rr-field');
      inp.value = '';
      inp.name = 'contact[Wearer ' + pad(n) + ' ' + key + ']';
      inp.id = 'rr-w' + n + '-' + key + '-row';
    });
    /* Re-pair every label to its input; cloneNode keeps the old `for`. */
    $$('.rr-f', clone).forEach(function (f) {
      var inp = $('input, select', f), lab = $('label', f);
      if (inp && lab) lab.setAttribute('for', inp.id);
    });
    wearerWrap.appendChild(clone);
    renumberWearers();
    if (focus) {
      var first = $('input, select', clone);
      if (first) first.focus();
      announce('Wearer ' + n + ' added.');
    }
    return clone;
  }

  if (addBtn && wearerWrap) {
    addBtn.addEventListener('click', function () {
      var count = wearerRows().length;
      if (addWearerRow(true)) {
        if (qtyInput && Number(qtyInput.value) === count) qtyInput.value = count + 1;
        updateWearerUI();
      }
    });
  }

  function renumberWearers() {
    var rows = wearerRows();
    rows.forEach(function (row, index) {
      var n = index + 1;
      row.setAttribute('data-rr-wearer', String(n));
      var number = $('.rr-wear__n', row);
      if (number) number.textContent = pad(n);
      $$('[data-rr-field]', row).forEach(function (input) {
        var key = input.getAttribute('data-rr-field');
        input.name = 'contact[Wearer ' + pad(n) + ' ' + key + ']';
        input.id = 'rr-w' + n + '-' + key + '-row';
        var label = $('label', input.closest('.rr-f'));
        if (label) label.htmlFor = input.id;
      });
      var remove = $('[data-rr-remove-wearer]', row);
      if (remove) {
        remove.hidden = rows.length === 1;
        remove.setAttribute('aria-label', remove.textContent + ' ' + ($('.rr-wear__head strong', row).textContent).trim());
      }
    });
  }

  if (wearerWrap) wearerWrap.addEventListener('click', function (event) {
    var button = event.target.closest('[data-rr-remove-wearer]');
    if (!button || wearerRows().length <= 1) return;
    var row = button.closest('[data-rr-wearer]');
    var rows = wearerRows();
    removedWearer = { row: row, index: rows.indexOf(row), previousQuantity: qtyInput ? qtyInput.value : '' };
    row.remove();
    if (qtyInput && Number(qtyInput.value) === rows.length) qtyInput.value = rows.length - 1;
    renumberWearers();
    var undo = $('[data-rr-wearer-undo]', form);
    if (undo) undo.hidden = false;
    updateWearerUI();
    var focusRow = wearerRows()[Math.min(removedWearer.index, wearerRows().length - 1)];
    var next = $('[data-rr-remove-wearer]', focusRow);
    if (next && !next.hidden) next.focus({ preventScroll: true });
    else if (addBtn) addBtn.focus({ preventScroll: true });
    announce('Wearer removed. Use Undo to restore their measurements.');
  });

  var undoWearer = $('[data-rr-undo-wearer]', form);
  if (undoWearer) undoWearer.addEventListener('click', function () {
    if (!removedWearer || wearerRows().length >= WEARER_CAP) return;
    var row = removedWearer.row;
    wearerWrap.insertBefore(row, wearerRows()[removedWearer.index] || null);
    if (qtyInput && Number(qtyInput.value) === wearerRows().length - 1) qtyInput.value = removedWearer.previousQuantity;
    removedWearer = null;
    $('[data-rr-wearer-undo]', form).hidden = true;
    renumberWearers();
    updateWearerUI();
    $('[data-rr-field="name"]', row).focus();
    announce('Wearer restored.');
  });

  function updateWearerUI() {
    var n = wearerRows().length;
    var hint = $('[data-rr-wearer-hint]', form);
    if (addBtn) addBtn.disabled = n >= WEARER_CAP;
    if (hint) hint.textContent = n >= WEARER_CAP ? 'For more than ' + WEARER_CAP + ', use the roster path above — it is quicker than filling this in.' : defaultWearerHint;
    if (undoWearer) undoWearer.disabled = n >= WEARER_CAP;
    update();
  }

  /* ============================================================== THE SUMMARY
     An index of the form. Every value here is read live from the form; none
     of it is stored. Empty lines stay as dimmed placeholders so the rail's
     height never changes as the visitor fills it in.
     ---------------------------------------------------------------------- */
  function fmtDate(v) {
    if (!v) return '';
    var p = v.split('-');
    if (p.length !== 3) return v;
    var M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return M[Number(p[1]) - 1] + ' ' + Number(p[2]) + ', ' + p[0];
  }

  function summary() {
    var mode = packageMode();
    var gear = radio('contact[Package]');
    if (mode === 'parts') {
      var parts = chosenParts();
      gear = parts.length ? parts.map(partLabel).join(', ') : 'Individual components';
    }

    var qty = qtyInput ? qtyInput.value.trim() : '';
    var from = fmtDate(val('contact[Needed by]'));
    var to = fmtDate(val('contact[Expected return]'));
    var dur = radio('contact[Rental duration]');

    var timing = '';
    if (from && to) timing = from + ' – ' + to;
    else if (from) timing = 'From ' + from;
    else if (dur) timing = dur;

    return {
      use_case: radio('contact[Rental purpose]'),
      gear: isSpecialty() ? 'Application-specific review' : gear,
      quantity: qty ? qty + (qty === '1' ? ' wearer' : ' wearers') : '',
      timing: timing,
      sizing: isSpecialty() ? 'Delta will review the required sizing' : radio('contact[Sizing method]')
    };
  }

  function paintSummary() {
    var s = summary();
    $$('[data-rr-sum]', root).forEach(function (el) {
      var k = el.getAttribute('data-rr-sum');
      var v = s[k];
      if (v) { el.textContent = v; el.removeAttribute('data-rr-empty'); }
      else { el.textContent = '—'; el.setAttribute('data-rr-empty', ''); }
    });

    /* Mobile bar: one line, the request state, never a price. */
    var bar = $('[data-rr-bar]', root);
    var barSum = $('[data-rr-barsum]', root);
    if (bar && barSum) {
      var bits = [s.gear, s.quantity, s.timing].filter(Boolean);
      var started = !!(s.use_case || s.gear);
      barSum.textContent = bits.length ? bits.join(' · ') : (s.use_case || bar.getAttribute('data-empty') || 'Start your request');
      bar.setAttribute('data-rr-show', started ? 'true' : 'false');
      bar.setAttribute('aria-hidden', started ? 'false' : 'true');
      $$('button', bar).forEach(function (b) { b.tabIndex = started ? 0 : -1; });
    }
  }

  /* ------------------------------------------------------------- persistence */
  function snapshot() {
    var d = { _wearerCount: wearerRows().length };
    $$('input, textarea, select', form).forEach(function (el) {
      if (!el.name || el.type === 'submit') return;
      if (el.type === 'checkbox') { if (el.checked) { if (!Array.isArray(d[el.name])) d[el.name] = []; d[el.name].push(el.value); } }
      else if (el.type === 'radio') { if (el.checked) d[el.name] = el.value; }
      else if (el.value) d[el.name] = el.value;
    });
    return d;
  }

  function restore(d) {
    if (!d) return;
    /* Rebuild cloned firefighter rows FIRST. Without this, a reload silently
       drops every measurement after the first wearer. */
    var most = Number(d._wearerCount) || 1;
    Object.keys(d).forEach(function (k) {
      var m = k.match(/Wearer (\d+)/);
      if (m) most = Math.max(most, Number(m[1]));
    });
    while (wearerRows().length < Math.min(most, WEARER_CAP)) {
      if (!addWearerRow(false)) break;
    }
    Object.keys(d).forEach(function (name) {
      if (name === '_wearerCount') return;
      var els = $$('[name="' + name.replace(/"/g, '\\"') + '"]', form);
      els.forEach(function (el) {
        if (el.type === 'radio' || el.type === 'checkbox') { el.checked = Array.isArray(d[name]) ? d[name].indexOf(el.value) !== -1 : el.value === d[name]; }
        else el.value = d[name];
      });
    });
  }

  /* ---------------------------------------------------- wearer requirements
     Scott Hacker, 2026-09-03: every field on Delta's sizing template is
     mandatory EXCEPT the item-specific sizes. A department may not be renting
     boots or gloves at all, so those two never become required.

     The garment measurements are all-or-nothing per firefighter: a row with a
     chest and no sleeve is worse than an empty row, because it looks answered.
     Starting a row therefore commits you to finishing it. An untouched row
     stays entirely optional - not every roster fills all twelve.
     ---------------------------------------------------------------------- */
  var CORE_FIELDS = ['name', 'height', 'weight', 'chest', 'sleeve', 'waist', 'inseam'];

  function syncWearerRequirements() {
    var partial = false;
    wearerRows().forEach(function (row) {
      var core = CORE_FIELDS
        .map(function (k) { return row.querySelector('[data-rr-field="' + k + '"]'); })
        .filter(Boolean);
      var filled = core.filter(function (el) { return String(el.value).trim(); }).length;
      var started = filled > 0;
      core.forEach(function (el) { el.required = started; });
      row.setAttribute('data-rr-started', started ? 'true' : 'false');
      if (started && filled < core.length) partial = true;
    });
    var note = $('[data-rr-wear-req]', form);
    if (note) note.hidden = !partial;
  }

  /* The return date cannot precede the needed-by date. */
  function syncDateBounds() {
    var from = form.querySelector('[name="contact[Needed by]"]');
    var to = form.querySelector('[name="contact[Expected return]"]');
    if (from && to) to.min = from.value || '';
  }

  /* ------------------------------------------------------------------ update */
  function syncRequestScope() {
    var specialty = isSpecialty();
    ['[data-rr-structural]', '[data-rr-size-step]'].forEach(function (selector) {
      var panel = form.querySelector(selector);
      if (!panel) return;
      panel.hidden = specialty;
      if (specialty) $$('input,select,textarea,button', panel).forEach(function (input) { input.disabled = true; });
      else if (selector === '[data-rr-size-step]') $$('[data-rr-sizing]', panel).forEach(function (input) { input.disabled = false; });
      else $$('input[data-rr-package]', panel).forEach(function (input) { input.disabled = false; });
    });
    var panel = form.querySelector('[data-rr-specialty]');
    var details = form.querySelector('[data-rr-application]');
    if (panel) panel.hidden = !specialty;
    if (details) { details.disabled = !specialty; details.required = specialty; }
    var component = form.querySelector('[data-rr-component]');
    if (component) component.setCustomValidity(!specialty && packageMode() === 'parts' && !chosenParts().length ? 'Choose at least one gear component.' : '');
  }

  function update() {
    syncReveals();
    renumberWearers();
    syncRequestScope();
    syncWearerRequirements();
    syncDateBounds();
    syncQtyChips();
    paintFigure();
    paintSummary();
    save(snapshot());
    if (completeSummary) carrySummary();
  }

  form.addEventListener('change', function (e) {
    var t = e.target;
    if (t.matches('[data-rr-scenario]')) track('use_case_selected', { use_case: t.value });
    if (t.matches('[data-rr-package]')) {
      track('package_selected', { package: t.value });
      trackOnce('builder_started', {});
    }
    if (t.matches('[data-rr-component]')) track('component_toggled', { component: t.value, on: t.checked });
    if (t.matches('[data-rr-sizing]')) track('sizing_route_selected', { route: t.value });
    if (t.matches('[data-rr-duration]')) track('duration_selected', { duration: t.value });
    if (t === qtyInput) track('quantity_entered', { quantity: t.value, method: 'typed' });
    update();
  });
  /* Typed text has to persist too. The change event only fires on commit, so
     without this every measurement, the organization, name, email and notes
     were dropped on reload while the radios and dates appeared to survive -
     which reads as "the page restored my request" while having silently lost
     most of it. Debounced so a long roster is not serialised per keystroke. */
  var saveTimer = null;
  form.addEventListener('input', function (e) {
    if (e.target === qtyInput) { syncQtyChips(); }
    paintSummary();
    if (e.target.hasAttribute('data-rr-field')) syncWearerRequirements();
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { save(snapshot()); }, 250);
  });

  /* Mobile bar must retract while a text field has focus, or it sits between
     the field and the on-screen keyboard on the measurements step. */
  form.addEventListener('focusin', function (e) {
    var bar = $('[data-rr-bar]', root);
    if (!bar) return;
    if (e.target.matches('input:not([type=radio]):not([type=checkbox]), textarea, select')) {
      bar.setAttribute('data-rr-focus', 'true');
    }
  });
  form.addEventListener('focusout', function () {
    var bar = $('[data-rr-bar]', root);
    if (bar) bar.removeAttribute('data-rr-focus');
  });

  /* Rail edit links scroll to and focus the step they name. */
  $$('.rr-rail__e', root).forEach(function (a) {
    a.addEventListener('click', function (e) {
      var id = a.getAttribute('href').slice(1);
      var target = document.getElementById(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
      var first = target.querySelector('input, select, textarea, button');
      if (first) setTimeout(function () { first.focus({ preventScroll: true }); }, prefersReduced() ? 0 : 380);
    });
  });

  function prefersReduced() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* -------------------------------------------------------------- submission
     Stage the derived values Delta cannot infer from the raw fields. Everything
     else already carries its own contact[...] name and posts natively.
     ------------------------------------------------------------------------ */
  var nativeRoot = document.querySelector('[data-rr-native-form]');
  var completeSummary = document.querySelector('[data-rr-complete-summary]');
  var fallbackForm = document.getElementById('rr-native-contact');
  var fallbackSummary = document.querySelector('[data-rr-native-summary]');
  var nativeSummary = null;
  var lastAutomaticSummary = '';
  var requestReference = 'RR-' + Date.now().toString(36).toUpperCase();

  function requestDetails() {
    var lines = ['Delta rental request', 'Request reference: ' + requestReference];
    var entries = new FormData(form);
    entries.forEach(function (value, key) {
      if (!String(value).trim() || !key.startsWith('contact[')) return;
      var label = key.slice(8, -1);
      if (/^Wearer /.test(label)) label = label.replace(/(height|chest|sleeve|waist|inseam)$/, '$1 (in)').replace(/weight$/, 'weight (lb)');
      lines.push(label + ': ' + String(value).trim());
    });
    var parts = chosenParts().map(partLabel).join(', ');
    if (parts) lines.push('Components: ' + parts);
    lines.push('Submitted from: ' + window.location.origin + window.location.pathname);
    return lines.join('\n');
  }

  function canCarrySummary() {
    return !!(radio('contact[Rental purpose]') && (isSpecialty() ? val('contact[Application details]') : radio('contact[Package]')) && qtyInput && qtyInput.value && (isSpecialty() || radio('contact[Sizing method]')) && form.checkValidity());
  }

  function carrySummary() {
    var text = requestDetails();
    if (completeSummary) completeSummary.value = text;
    if (fallbackSummary) fallbackSummary.value = text;
    if (!nativeSummary || !canCarrySummary()) return;
    if (nativeSummary.value === text) return;
    if (nativeSummary.value && nativeSummary.value !== lastAutomaticSummary) {
      var status = document.querySelector('[data-rr-copy-status]');
      if (status && text !== lastAutomaticSummary) status.textContent = status.getAttribute('data-manual-summary');
      return;
    }
    var proto = nativeSummary.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
    var setter = Object.getOwnPropertyDescriptor(proto, 'value').set;
    setter.call(nativeSummary, text);
    nativeSummary.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    nativeSummary.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
    lastAutomaticSummary = text;
  }

  function rootsWithin(root) {
    var roots = [root];
    for (var i = 0; i < roots.length; i++) {
      roots[i].querySelectorAll('*').forEach(function (node) { if (node.shadowRoot) roots.push(node.shadowRoot); });
    }
    return roots;
  }

  function findNativeForm() {
    if (!nativeRoot) return false;
    var roots = rootsWithin(nativeRoot);
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-testid="banner-success"]')) {
        trackOnce('request_success', {});
        try { window.sessionStorage.removeItem(STORE_KEY); } catch (e) {}
        var fallback = root.querySelector('[data-rr-form-fallback]');
        if (fallback) fallback.hidden = true;
        return true;
      }
      var inputs = Array.prototype.slice.call(roots[i].querySelectorAll('textarea,input'));
      var found = inputs.find(function (input) { return /rental_request_summary/.test(input.name + ' ' + input.id); });
      if (!found) continue;
      nativeSummary = found;
      var fallback = root.querySelector('[data-rr-form-fallback]');
      if (fallback) fallback.hidden = true;
      var nativeTree = found.getRootNode();
      if (nativeTree !== document && !nativeTree.__deltaRentalWatched) {
        nativeTree.__deltaRentalWatched = true;
        new MutationObserver(findNativeForm).observe(nativeTree, {childList:true, subtree:true});
      }
      carrySummary();
      var actualForm = found.closest('form');
      if (actualForm && !actualForm.hasAttribute('data-rr-tracked')) {
        actualForm.setAttribute('data-rr-tracked', 'true');
        actualForm.addEventListener('submit', function (event) {
          update();
          revealInvalidStep();
          if (radio('contact[Rental purpose]') && !form.reportValidity()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            return;
          }
          carrySummary();
          if (!actualForm.checkValidity()) return;
          track('request_attempt', {});
        }, true);
      }
      return true;
    }
    return false;
  }

  function revealInvalidStep() {
    var invalid = form.querySelector('input:invalid, select:invalid, textarea:invalid');
    if (invalid) root.dispatchEvent(new CustomEvent('rr:invalid', { bubbles: true, detail: { field: invalid } }));
  }

  function continueRequest() {
    update();
    revealInvalidStep();
    if (!form.reportValidity()) return;
    findNativeForm();
    carrySummary();
    root.dispatchEvent(new CustomEvent('rr:contact-open', { bubbles: true }));
    var contact = document.getElementById('rr-contact');
    if (contact) contact.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
    track('contact_details_opened', {});
  }
  form.addEventListener('submit', function (event) { event.preventDefault(); continueRequest(); });
  $$('[data-rr-continue]', form).forEach(function (button) { button.addEventListener('click', continueRequest); });
  form.addEventListener('change', carrySummary);
  form.addEventListener('input', carrySummary);
  if (fallbackForm) fallbackForm.addEventListener('submit', function (event) {
    update();
    revealInvalidStep();
    if (!form.reportValidity()) {
      event.preventDefault();
      return;
    }
    carrySummary();
    if (fallbackForm.checkValidity()) track('request_attempt', {});
  });
  if (nativeRoot) {
    var nativeAttempts = 0;
    var nativeTimer = setInterval(function () { nativeAttempts++; if (findNativeForm() || nativeAttempts >= 60) clearInterval(nativeTimer); }, 500);
    findNativeForm();
    new MutationObserver(findNativeForm).observe(nativeRoot, {childList:true, subtree:true});
  }
  var copyButton = document.querySelector('[data-rr-copy]');
  if (copyButton) copyButton.addEventListener('click', function () {
    carrySummary();
    var status = document.querySelector('[data-rr-copy-status]');
    function selectSummary() {
      var disclosure = completeSummary.closest('details');
      if (disclosure) disclosure.open = true;
      completeSummary.focus();
      completeSummary.select();
      if (status) status.textContent = copyButton.getAttribute('data-copy-failed');
    }
    if (!navigator.clipboard) { selectSummary(); return; }
    navigator.clipboard.writeText(completeSummary.value).then(function () {
      if (status) status.textContent = copyButton.getAttribute('data-copied');
    }).catch(function () {
      selectSummary();
    });
  });

  /* Outbound CTA instrumentation, page-wide. */
  $$('[data-rr-ev]').forEach(function (el) {
    el.addEventListener('click', function () { track(el.getAttribute('data-rr-ev'), {}); });
  });

  /* ------------------------------------------------------------------- boot */
  var params = new URLSearchParams(window.location.search);
  var deep = params.get('use_case');
  restore(load());
  if (deep) {
    var r = $$('input[name="contact[Rental purpose]"]', form).find(function (input) { return input.value === deep; });
    if (r) r.checked = true;
  }
  update();
  carrySummary();

})();

(function () {
  'use strict';

  function boot() {
    var root = document.querySelector('[data-rental-studio]');
    if (!root || root.hasAttribute('data-rental-enhanced')) return;
    var form = root.querySelector('#rr-request-form');
    var panels = {};
    [1, 2, 3].forEach(function (step) {
      panels[step] = root.querySelector('[data-rental-panel="' + step + '"]');
    });
    if (!form || !panels[1] || !panels[2] || !panels[3]) return;

    var current = 1;
    var nav = Array.prototype.slice.call(root.querySelectorAll('[data-rental-step]'));
    var reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var PACKAGE_PARTS = {
      full: ['coat', 'pant', 'helmet', 'hood', 'gloves', 'boots'],
      coatpant: ['coat', 'pant']
    };

    function all(selector, context) {
      return Array.prototype.slice.call((context || root).querySelectorAll(selector));
    }

    function purpose() {
      return form.querySelector('input[data-rr-scenario]:checked');
    }

    function chosenPackage() {
      return form.querySelector('input[data-rr-package]:checked');
    }

    function specialty() {
      var selected = purpose();
      return !!selected && selected.value === 'Industrial or aluminized application';
    }

    function selectedParts() {
      if (specialty()) return [];
      var selected = chosenPackage();
      var mode = selected ? selected.getAttribute('data-rr-package') : '';
      if (PACKAGE_PARTS[mode]) return PACKAGE_PARTS[mode].slice();
      if (mode !== 'parts') return [];
      return all('input[data-rr-component]:checked', form).map(function (input) {
        return input.getAttribute('data-rr-component');
      });
    }

    function partLabel(key) {
      var input = all('input[data-rr-component]', form).find(function (item) {
        return item.getAttribute('data-rr-component') === key;
      });
      return input ? input.value : key;
    }

    var measureDialog = root.querySelector('[data-rr-measure-dialog]');
    var measureTrigger = null;
    function showMeasurement(key) {
      var valid = all('[data-rr-measure-card]').some(function (card) { return card.getAttribute('data-rr-measure-card') === key; });
      if (!valid) key = 'other';
      all('[data-rr-measure-card]').forEach(function (card) { card.hidden = card.getAttribute('data-rr-measure-card') !== key; });
      all('[data-rr-measure-tab]').forEach(function (button) { button.setAttribute('aria-pressed', button.getAttribute('data-rr-measure-tab') === key ? 'true' : 'false'); });
    }
    root.addEventListener('click', function (event) {
      var open = event.target.closest('[data-rr-measure-open]');
      if (open && measureDialog) {
        measureTrigger = open;
        showMeasurement(open.getAttribute('data-rr-measure-open'));
        measureDialog.showModal();
      }
      var tab = event.target.closest('[data-rr-measure-tab]');
      if (tab) showMeasurement(tab.getAttribute('data-rr-measure-tab'));
      if (event.target.closest('[data-rr-measure-close]') && measureDialog) measureDialog.close();
    });
    if (measureDialog) measureDialog.addEventListener('close', function () {
      if (measureTrigger && measureTrigger.isConnected) measureTrigger.focus({ preventScroll: true });
    });

    function syncScene() {
      var selected = chosenPackage();
      var isSpecialty = specialty();
      var mode = isSpecialty ? 'specialty' : (selected ? selected.getAttribute('data-rr-package') : 'none');
      var parts = selectedParts();
      var selection = isSpecialty ? root.getAttribute('data-rental-specialty-selection') : (selected ? selected.value : root.getAttribute('data-rental-empty-selection'));
      var description = isSpecialty
        ? root.getAttribute('data-rental-specialty-components')
        : (parts.length ? parts.map(partLabel).join(' · ') : (mode === 'parts' ? root.getAttribute('data-rental-parts-empty') : root.getAttribute('data-rental-empty-components')));
      root.setAttribute('data-rental-package', mode);
      root.setAttribute('data-rental-parts', parts.join(' '));
      all('[data-rental-selection]').forEach(function (element) { element.textContent = selection; });
      all('[data-rental-components]').forEach(function (element) { element.textContent = description; });
      all('[data-rental-part]').forEach(function (button) {
        button.setAttribute('aria-pressed', !isSpecialty && parts.indexOf(button.getAttribute('data-rental-part')) !== -1 ? 'true' : 'false');
        button.setAttribute('aria-disabled', isSpecialty ? 'true' : 'false');
        if (button.tagName === 'BUTTON') button.disabled = isSpecialty;
      });
    }

    function panelNumber(element) {
      var panel = element && element.closest ? element.closest('[data-rental-panel]') : null;
      return panel && root.contains(panel) ? Number(panel.getAttribute('data-rental-panel')) : 0;
    }

    function focusElement(element) {
      if (!element || typeof element.focus !== 'function') return;
      if (!element.matches('input, select, textarea, button, a[href], [tabindex]')) element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
    }

    function scrollTo(element) {
      if (element) element.scrollIntoView({ behavior: reduced && reduced.matches ? 'auto' : 'smooth', block: 'start' });
    }

    function showStep(step, options) {
      if (!panels[step]) return;
      options = options || {};
      current = step;
      root.setAttribute('data-rental-active', String(step));
      [1, 2, 3].forEach(function (number) { panels[number].hidden = number !== step; });
      nav.forEach(function (button) {
        var number = Number(button.getAttribute('data-rental-step'));
        if (number === step) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      var heading = panels[step].querySelector('[data-rental-panel-heading]') || panels[step];
      if (options.focus) focusElement(heading);
      if (options.scroll) scrollTo(options.target || heading);
      syncScene();
    }

        function firstInvalid(panel) {
      return panel ? panel.querySelector('input:invalid, select:invalid, textarea:invalid') : null;
    }

    function revealInvalid(field, report) {
      var step = panelNumber(field);
      if (!step) return false;
      showStep(step);
      var parent = field.parentElement;
      while (parent && parent !== root) {
        if (parent.tagName === 'DETAILS') parent.open = true;
        parent = parent.parentElement;
      }
      focusElement(field);
      if (report && typeof field.reportValidity === 'function') field.reportValidity();
      return true;
    }

    function validateBefore(step, report) {
      for (var number = 1; number < step; number++) {
        var invalid = firstInvalid(panels[number]);
        if (invalid) {
          if (report) revealInvalid(invalid, true);
          return invalid;
        }
      }
      return null;
    }

    function openContact() {
      var button = form.querySelector('[data-rr-continue]');
            if (button) button.click();
    }

    function navigate(step, options) {
      if (!panels[step]) return;
      if (step > 1 && validateBefore(step, true)) return;
      if (step === 3) { openContact(); return; }
      showStep(step, options || { focus: true, scroll: true });
    }

    function changed(input) {
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function choosePurpose(value) {
      var selected = all('input[data-rr-scenario]', form).find(function (input) { return input.value === value; });
      if (!selected) return;
      selected.checked = true;
      changed(selected);
      showStep(1, { focus: true, scroll: true });
    }

    function togglePart(key) {
      if (specialty()) return;
      var partsOption = form.querySelector('input[data-rr-package="parts"]');
      var component = all('input[data-rr-component]', form).find(function (input) { return input.getAttribute('data-rr-component') === key; });
      if (!partsOption || !component) return;
      var before = selectedParts();
      var alreadyParts = partsOption.checked;
      if (!alreadyParts) {
        all('input[data-rr-component]', form).forEach(function (input) {
          input.checked = before.indexOf(input.getAttribute('data-rr-component')) !== -1;
        });
      }
      partsOption.checked = true;
      changed(partsOption);
      component.checked = !component.checked;
      changed(component);
      showStep(1, { focus: false, scroll: false });
    }

    function hashTarget(hash) {
      if (!hash || hash.charAt(0) !== '#') return null;
      try { return document.getElementById(decodeURIComponent(hash.slice(1))); }
      catch (error) { return null; }
    }

    function revealHash(hash, initial) {
      var target = hashTarget(hash);
      if (!target || !root.contains(target)) return false;
      var step = panelNumber(target);
      if (!step) {
        if (hash === '#rr-request' || hash === '#rr-paths' || hash === '#rr-process') {
          showStep(1, { focus: !initial, scroll: !initial, target: target });
        }
        return false;
      }
      var invalid = step > 1 ? validateBefore(step, !initial) : null;
      if (invalid) {
        if (initial) showStep(panelNumber(invalid) || 1);
        return true;
      }
      if (step === 3 && !initial) openContact();
      else showStep(step, { focus: !initial, scroll: !initial, target: target });
      return true;
    }

    root.addEventListener('rr:contact-open', function () {
      showStep(3, { focus: true, scroll: false });
    });
    root.addEventListener('rr:invalid', function (event) {
      var field = event.detail && event.detail.field;
      if (field && form.contains(field)) revealInvalid(field, false);
    });

        document.addEventListener('click', function (event) {
      var target = event.target && event.target.closest ? event.target : null;
      if (!target || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      var control = target.closest('[data-rental-step], [data-rental-next], [data-rental-back], [data-rental-purpose], [data-rental-part], [data-rental-contact-edit]');
      if (control && (root.contains(control) || control.hasAttribute('data-rental-purpose'))) {
        if (control.disabled || control.getAttribute('aria-disabled') === 'true') return;
        event.preventDefault();
        if (control.hasAttribute('data-rental-step')) navigate(Number(control.getAttribute('data-rental-step')));
        else if (control.hasAttribute('data-rental-next')) navigate(Math.min(current + 1, 3));
        else if (control.hasAttribute('data-rental-back')) showStep(Math.max(current - 1, 1), { focus: true, scroll: true });
        else if (control.hasAttribute('data-rental-purpose')) choosePurpose(control.getAttribute('data-rental-purpose'));
        else if (control.hasAttribute('data-rental-part')) togglePart(control.getAttribute('data-rental-part'));
        else showStep(1, { focus: true, scroll: true });
        return;
      }
      var link = target.closest('a[href^="#"]');
      if (!link) return;
      var hash = link.getAttribute('href');
      if (!/^#rr-step-/.test(hash) && hash !== '#rr-contact') return;
      if (!revealHash(hash, false)) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

        form.noValidate = true;
    form.addEventListener('submit', function (event) {
      if (current !== 1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      navigate(2);
    }, true);
    form.addEventListener('change', syncScene);
    form.addEventListener('input', syncScene);
    window.addEventListener('hashchange', function () { revealHash(window.location.hash, false); });

    root.setAttribute('data-rental-enhanced', 'true');
    nav.forEach(function (button) {
      var step = Number(button.getAttribute('data-rental-step'));
      if (!panels[step]) return;
      if (!panels[step].id) panels[step].id = 'rr-rental-panel-' + step;
      button.setAttribute('aria-controls', panels[step].id);
    });
    showStep(root.querySelector('[data-rr-contact-success]') ? 3 : 1);
    revealHash(window.location.hash, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
